import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { ActionType, RootState } from "../reducer";
import {
  AiChatLoading,
  AiEvent,
  AiEventType,
  ConnectionStatus,
} from "../types/ai-event";
import { useAuthToken } from "../hook";
import { useAiPanelOpen } from "../hook/ai-panel";
import {
  ConnStatus,
  createAiConnectionController,
} from "./aiConnectionController";
import { useAiIdentity } from "../slices/ui";
import {
  continuityActions,
  DROPPED_BEFORE_START_MESSAGE,
  identityCredentialKey,
  identityKey,
  resumedIdleEvent,
  resumeOnOpen,
} from "./sessionContinuity";
import { chatPhase } from "./chatPhase";
import { isStaleContinuityFrame, isStaleTurnFrame } from "./frameChat";
import { sessionCreateFrame } from "./sessionCreate";
import { frameToAiEvent } from "./aiFrames";
import { measureSince } from "./perfMarks";
import { chatModel } from "../hook/agentProvider";
import { AGENT_WS_URL as WS_URL } from "../api/agentUrl";

type Controller = ReturnType<typeof createAiConnectionController>;

// What sendCreate did with session.create: sent it now, found one already sent for this socket, or
// could not send it because the socket is not open.
export type CreateResult = "sent" | "already" | "dropped";

interface AiConnectionContextValue {
  status: ConnStatus;
  // true when the frame went out; false when the socket was not open (nothing was sent).
  send: (obj: any) => boolean;
  sendCreate: () => CreateResult;
  retryNow: () => void;
  sendQuestionReply: (requestID: string, answers: string[][]) => void;
  activate: () => void;
  // True once this provider has reached CONNECTED; a later drop is a reconnect, not a first connect.
  hadConnected: boolean;
  // The resume/history marker the chat lifecycle reads.
  chatLoading: AiChatLoading;
}

const AiConnectionContext = createContext<AiConnectionContextValue | null>(null);

export function useAiConnection(): AiConnectionContextValue {
  const ctx = useContext(AiConnectionContext);
  if (!ctx) {
    throw new Error("useAiConnection must be used within <AiConnectionProvider>");
  }
  return ctx;
}

const STATUS_MAP: Record<ConnStatus, ConnectionStatus> = {
  connecting: ConnectionStatus.CONNECTING,
  connected: ConnectionStatus.CONNECTED,
  disconnected: ConnectionStatus.DISCONNECTED,
};

export const AiConnectionProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  // Read events on demand (getState) instead of subscribing: subscribing would re-render the
  // provider — and every useAiConnection consumer — on every frame.
  const store = useStore<RootState>();
  const [open] = useAiPanelOpen();
  const [hasActivated, setHasActivated] = useState(false);
  const [authToken] = useAuthToken();
  const selectedModel = useSelector((s: RootState) => s.ui.selectedModel);
  const selectedDevcontainer = useSelector((s: RootState) => s.ui.selectedDevcontainer);
  const selectedProvider = useSelector((s: RootState) => s.ui.selectedProvider);
  const activeSessionId = useSelector((s: RootState) => s.ui.activeSessionId);
  const aiIdentity = useAiIdentity();

  const [status, setStatus] = useState<ConnStatus>("disconnected");
  const [hadConnected, setHadConnected] = useState(false);
  const chatLoading = useSelector((s: RootState) => s.ui.aiChatLoading);

  // Latest-deps refs so the long-lived controller closures always read current
  // values without rebuilding the controller on every render.
  const authTokenRef = useRef(authToken);
  const selectedModelRef = useRef(selectedModel);
  const selectedDevcontainerRef = useRef(selectedDevcontainer);
  const selectedProviderRef = useRef(selectedProvider);
  const aiIdentityRef = useRef(aiIdentity);
  // The Bound Session (agent-service Terms) — read when a socket opens, to resume it.
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  authTokenRef.current = authToken;
  selectedModelRef.current = selectedModel;
  selectedDevcontainerRef.current = selectedDevcontainer;
  selectedProviderRef.current = selectedProvider;
  aiIdentityRef.current = aiIdentity;

  // U.3 — has an explicit session.create already been sent for the current
  // socket? Reset on disconnect and on a client-initiated user.reset/
  // session.reset so a fresh chat sends a fresh session.create.
  const createdRef = useRef(false);
  // Set when a user.message goes out; the status_idle that ends that Run re-requests session.list.
  const listAfterIdleRef = useRef(false);

  // onMessage routing. Uses a ref so the controller closure always dispatches via the live
  // dispatch and can re-send session.list through the live controller.
  const controllerRef = useRef<Controller | null>(null);

  const onMessage = useCallback(
    (data: any) => {
      const addEvent = (event: AiEvent) =>
        dispatch({ type: ActionType.ADD_AI_EVENT, event });
      const events = () => store.getState().ui.aiEvents;
      // A turn frame of a chat the panel has since left (New chat, a row pick) is dropped; the chat keeps running in its workspace and
      // its transcript is replayed when it is opened again.
      if (isStaleTurnFrame(data, store.getState().ui.activeSessionId)) return;
      // A resume answer, a failed resume or a bound answer for a chat the panel is no longer on is dropped (the last click wins).
      if (isStaleContinuityFrame(data, store.getState().ui.activeSessionId)) return;
      if (data.type === "agent.message" || data.type === "agent.tool_use") {
        const event = frameToAiEvent(data);
        if (event) dispatch({ type: ActionType.UPSERT_AI_EVENT, event });
      } else if (data.type === "agent.question") {
        addEvent({
          type: AiEventType.QUESTION,
          requestID: data.requestID,
          questions: data.questions,
          timestamp: data.timestamp || Date.now(),
        });
      } else if (data.type === "agent.tool_result" || data.type === "session.background_tasks") {
        const event = frameToAiEvent(data);
        if (event) addEvent(event);
      } else if (data.type === "session.replay") {
        // One History Page in one frame (agent-chat-speed Decisions 1–2): the newest page of a
        // resumed chat replaces the transcript, an older page (prepend) goes above it — one
        // dispatch, one render. A page for a chat the panel has since left is dropped.
        if (data.sessionId !== store.getState().ui.activeSessionId) return;
        const pageEvents: AiEvent[] = [];
        const frames: any[] = Array.isArray(data.events) ? data.events : [];
        for (const frame of frames) {
          const event = frameToAiEvent(frame);
          if (event) pageEvents.push(event);
        }
        dispatch({
          type: data.prepend === true ? ActionType.PREPEND_AI_EVENTS : ActionType.SET_AI_EVENTS_BATCH,
          events: pageEvents,
          cursor: typeof data.cursor === "string" ? data.cursor : null,
          hasMore: data.hasMore === true,
        });
        if (data.prepend !== true) measureSince("ai-resume-click", "ai-resume-to-paint", true);
        dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: null });
      } else if (data.type === "session.history_error") {
        if (data.sessionId !== store.getState().ui.activeSessionId) return;
        dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: null });
        dispatch({
          type: ActionType.SET_AI_HISTORY_ERROR,
          error: typeof data.error === "string" ? data.error : "history request failed",
        });
      } else if (data.type === "session.provisioning") {
        // The server's immediate acknowledgement of session.create / session.resume. The click
        // already showed "Starting workspace…" locally; add the status only if nothing is in
        // progress (never late, on top of a Run that already started).
        if (!chatPhase(events()).busy) {
          addEvent({ type: AiEventType.STATUS_PROVISIONING, timestamp: data.timestamp || Date.now() });
        }
        measureSince("ai-send-click", "ai-send-to-provisioning-ack", false);
      } else if (data.type === "session.status_running") {
        // A resume's provisioning/bind is complete and the server is about to read the newest
        // History Page; the chat is loading history until the replay lands.
        if (store.getState().ui.aiChatLoading === "resuming") {
          dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: "loading-history" });
        }
        addEvent({
          type: AiEventType.STATUS_RUNNING,
          timestamp: data.timestamp || Date.now(),
        });
      } else if (data.type === "session.status_idle") {
        // Re-list only when this idle ends a Run THIS panel started (a user.message went out since
        // the last list): that keeps a new chat's row appearing live and last-message-at fresh
        // (commit 09606fa, journey spec step 3), while the idle that ends a resume replay — no Run;
        // today's server still sends status_running before the replay (server.ts:805), so status
        // order cannot tell — no longer costs a session.list.
        const endedRun = listAfterIdleRef.current;
        listAfterIdleRef.current = false;
        addEvent({
          type: AiEventType.STATUS_IDLE,
          timestamp: data.timestamp || Date.now(),
        });
        if (endedRun) {
          controllerRef.current?.send({ type: "session.list" });
          // The bridge records the Run's end (status idle, the goal's state) just after it sends this idle, so the list above
          // can still show "Working"; ask again once that write has landed (omega-ai-agent-service src/sessionFacts.ts noteFacts).
          window.setTimeout(() => controllerRef.current?.send({ type: "session.list" }), 3000);
        }
      } else if (data.type === "session.status_terminated") {
        addEvent({
          type: AiEventType.STATUS_TERMINATED,
          timestamp: data.timestamp || Date.now(),
        });
      } else if (data.type === "session.error") {
        addEvent({
          type: AiEventType.SESSION_ERROR,
          error: data.error,
          code: typeof data.code === "string" ? data.code : undefined,
          timestamp: data.timestamp || Date.now(),
        });
      } else if (data.type === "session.created") {
        // U.3 — the session/pod is now provisioned. No further action needed
        // here; the picker-lock selectors react to the existing "session
        // active" signals (activeSessionId / session history).
      } else if (data.type === "session.status_progress") {
        // U.5 — granular provisioning progress; rendered in the single stable
        // pinned indicator (TypingIndicator), never as a persistent bubble.
        addEvent({
          type: AiEventType.STATUS_PROGRESS,
          stage: data.stage,
          text: data.text,
          timestamp: data.timestamp || Date.now(),
        });
      } else if (data.type === "session.list") {
        dispatch({
          type: ActionType.SET_SESSION_HISTORY,
          sessions: data.sessions ?? [],
        });
      } else if (
        data.type === "session.bound" ||
        data.type === "session.resumed" ||
        data.type === "session.reset" ||
        data.type === "session.resume_failed"
      ) {
        // Session continuity: track the Bound Session; a failed resume is shown as a reset and
        // the next message sends a fresh session.create.
        if (data.type === "session.resume_failed") createdRef.current = false;
        // A resume that finds the Run still going (busy) is a Run, not a workspace start: the
        // label turns to "Thinking…" even if the server sent no status_running for it.
        if (data.type === "session.resumed" && data.busy === true && chatPhase(events()).provisioning) {
          addEvent({ type: AiEventType.STATUS_RUNNING, timestamp: data.timestamp || Date.now() });
        }
        const settled = resumedIdleEvent(data, events(), data.timestamp || Date.now());
        if (settled) addEvent(settled);
        for (const action of continuityActions(data, Date.now())) dispatch(action);
      }
    },
    [dispatch, store]
  );
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // Build the controller exactly once (StrictMode/double-mount safe).
  if (controllerRef.current === null) {
    controllerRef.current = createAiConnectionController({
      makeSocket: (u) => new WebSocket(u),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (id) => window.clearTimeout(id),
      now: () => Date.now(),
      buildUrl: () => {
        const id = aiIdentityRef.current;
        const cred = id.kind === "service-account" ? id.apiKey : authTokenRef.current;
        const model = selectedModelRef.current;
        return cred
          ? `${WS_URL}?token=${encodeURIComponent(cred)}&model=${encodeURIComponent(model)}`
          : WS_URL;
      },
      onStatus: (s) => {
        setStatus(s);
        if (s === "connected") setHadConnected(true);
        if (s === "disconnected") {
          createdRef.current = false;
          // A socket that drops before the server bound a Session takes the workspace start with
          // it; say so instead of leaving "Starting workspace…" up forever.
          if (activeSessionIdRef.current === null && chatPhase(store.getState().ui.aiEvents).provisioning) {
            dispatch({
              type: ActionType.ADD_AI_EVENT,
              event: { type: AiEventType.SESSION_ERROR, error: DROPPED_BEFORE_START_MESSAGE, timestamp: Date.now() },
            });
          }
          // A stale event-only Run must not survive the drop. It is suspended here; a real Run is
          // re-announced by the server after the resume replay.
          if (chatPhase(store.getState().ui.aiEvents).busy) {
            dispatch({
              type: ActionType.ADD_AI_EVENT,
              event: { type: AiEventType.STATUS_IDLE, timestamp: Date.now() },
            });
          }
        }
        dispatch({
          type: ActionType.SET_AI_CONNECTION_STATUS,
          status: STATUS_MAP[s],
        });
        // A (re)opened socket resumes the Bound Session instead of letting the next message mint
        // a new one behind a transcript that still looks continuous. The resumed socket already
        // has a Session, so no session.create is sent for it.
        if (s === "connected") {
          const plan = resumeOnOpen(activeSessionIdRef.current, Date.now());
          for (const action of plan.actions) dispatch(action);
          for (const frame of plan.frames) controllerRef.current?.send(frame);
          if (plan.frames.length > 0) createdRef.current = true;
        }
      },
      onMessage: (data) => onMessageRef.current(data),
      onConnError: (reason) =>
        dispatch({
          type: ActionType.ADD_AI_EVENT,
          event: {
            type: AiEventType.CONNECTION_ERROR,
            error: reason,
            retryable: true,
            timestamp: Date.now(),
          },
        }),
      getExpiry: () => null,
      refreshToken: async () => false,
    });
  }

  // Activation gate — port of the old hasActivated/open behavior.
  useEffect(() => {
    if (open && !hasActivated) setHasActivated(true);
  }, [open, hasActivated]);

  // A throttled background tab or a network change can leave the socket looking open or a backoff
  // timer waiting for minutes. A visibility/focus/online probe cancels the wait or health-checks
  // the open socket; a burst of events runs at most one probe per second.
  const lastProbeRef = useRef(0);
  useEffect(() => {
    if (!hasActivated) return;
    const probe = () => {
      const now = Date.now();
      if (now - lastProbeRef.current < 1000) return;
      lastProbeRef.current = now;
      controllerRef.current?.reconnectNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") probe();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", probe);
    window.addEventListener("online", probe);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", probe);
      window.removeEventListener("online", probe);
    };
  }, [hasActivated]);

  // A credential is required before the socket can authenticate: the JWT for a
  // self identity, or the api-key for a service account. Connecting before it's
  // ready produces a tokenless WS that the agent-service rejects (auth-failed),
  // which is exactly the race the full-page /chat hit (activate() fires on mount,
  // before getAccessTokenSilently() has resolved the token).
  const hasCredential =
    aiIdentity.kind === "service-account" ? !!aiIdentity.apiKey : !!authToken;

  // Start the controller once activated AND a credential exists; stop on unmount.
  useEffect(() => {
    if (!hasActivated || !hasCredential) return;
    const controller = controllerRef.current!;
    controller.start();
    return () => {
      controller.stop();
    };
  }, [hasActivated, hasCredential]);

  // A model change does not reconnect: the ?model= on the connect URL is only the connection's
  // default. session.create carries its own model and session.resume uses the stored chat's
  // model, so replacing the socket here would only drop the Bound Session and clear the transcript.

  // Reconnect when the acting identity changes so the new WS URL carries the
  // correct ?token= credential (JWT for self, api-key for a service account).
  // A DIFFERENT Account's Sessions are not this chat's: start a new chat explicitly (clear +
  // unbind) instead of resuming a Session the new Account can't own. Re-picking the same Account
  // (a new identity object, same key) keeps the chat and resumes it on the reconnect.
  // Only a changed credential needs a new socket: re-picking the identity already in use (a new
  // object, same key) must not tear down the connection and replay the chat.
  const identityKeyRef = useRef(identityKey(aiIdentity));
  const identityCredentialRef = useRef(identityCredentialKey(aiIdentity));
  useEffect(() => {
    const key = identityKey(aiIdentity);
    const credential = identityCredentialKey(aiIdentity);
    const accountChanged = key !== identityKeyRef.current;
    const credentialChanged = credential !== identityCredentialRef.current;
    identityKeyRef.current = key;
    identityCredentialRef.current = credential;
    if (!hasActivated || !credentialChanged) return;
    if (accountChanged) {
      dispatch({ type: ActionType.CLEAR_AI_EVENTS });
      dispatch({ type: ActionType.SET_ACTIVE_SESSION_ID, sessionId: null });
      // The previous Account's chat list is not this Account's: drop it until session.list refills it.
      dispatch({ type: ActionType.SET_SESSION_HISTORY, sessions: [], loaded: false });
    }
    controllerRef.current?.retryNow();
  }, [aiIdentity]);

  // Dev-only test hooks: a reliable socket drop (normal onclose→backoff path,
  // manualClosed stays false → auto-recovery) and a status read. Used by the
  // real-stack Playwright connection e2e. Never present in production builds.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    (window as any).__aiConnDrop = () => controllerRef.current?.dropForTest();
    (window as any).__aiConnStatus = () => status;
    return () => {
      delete (window as any).__aiConnDrop;
      delete (window as any).__aiConnStatus;
    };
  }, [status]);

  const send = useCallback((obj: any): boolean => {
    // A client-initiated reset means the server-side session goes away — the
    // next chat needs a fresh session.create.
    if (obj?.type === "user.reset" || obj?.type === "session.reset") {
      createdRef.current = false;
    }
    const sent = controllerRef.current?.send(obj) ?? false;
    // A resumed socket already has a Session (as on the reconnect path above): no session.create.
    if (sent && obj?.type === "session.resume") createdRef.current = true;
    if (sent && obj?.type === "user.message") listAfterIdleRef.current = true;
    return sent;
  }, []);
  const retryNow = useCallback(() => controllerRef.current?.retryNow(), []);
  const sendQuestionReply = useCallback(
    (requestID: string, answers: string[][]) => {
      controllerRef.current?.send({ type: "user.question_reply", requestID, answers });
    },
    []
  );
  const activate = useCallback(() => setHasActivated(true), []);

  // U.3 — explicit session.create, sent immediately before the first
  // user.message (never lazily inside it). Idempotent per socket (reset on disconnect /
  // user.reset). Marked created ONLY when the frame went out: a dropped create used to set the
  // flag anyway, so the next message on the reconnected socket skipped session.create and the
  // server's lazy fallback started a pod without the picked devcontainer.
  const sendCreate = useCallback((): CreateResult => {
    if (createdRef.current) return "already";
    const sent =
      controllerRef.current?.send(
        sessionCreateFrame({
          model: chatModel(),
          devcontainer: selectedDevcontainerRef.current ?? undefined,
          modelProvider: selectedProviderRef.current,
        }),
      ) ?? false;
    if (sent) createdRef.current = true;
    return sent ? "sent" : "dropped";
  }, []);

  // One value object per real change, so consumers do not re-render on unrelated provider renders.
  const value = useMemo(
    () => ({ status, send, sendCreate, retryNow, sendQuestionReply, activate, hadConnected, chatLoading }),
    [status, send, sendCreate, retryNow, sendQuestionReply, activate, hadConnected, chatLoading]
  );

  return (
    <AiConnectionContext.Provider value={value}>
      {children}
    </AiConnectionContext.Provider>
  );
};
